/**
 * Course Notes admin hub — uploads multiple PDFs per brand.
 *
 * Lives in Staff Dashboard → Content & Pricing → Course Notes. Each brand
 * is a tab, each tab renders a `CourseNotesManager`. The installer-facing
 * view of the PDF stays at `/notes/:brand` (view + download only).
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpenText, Home } from 'lucide-react';
import AuthContext from '@/contexts/AuthContext';
import CourseNotesManager from '@/components/CourseNotesManager';
import { Button } from '@/components/ui/button';

const BRANDS = [
  { id: 'probound', name: 'ProBound', color: '#1f4030',  accent: '#D4A24B' },
  { id: 'proepoxy', name: 'ProEpoxy', color: '#5b21b6',  accent: '#a78bfa' },
  { id: 'prodecor', name: 'ProDecor', color: '#9a3412',  accent: '#fb923c' },
];

export default function StaffCourseNotes() {
  const navigate = useNavigate();
  const { isStaff, user } = useContext(AuthContext);
  const [tab, setTab] = useState('probound');

  useEffect(() => {
    if (!isStaff) navigate('/');
  }, [isStaff, navigate]);

  if (!isStaff) return null;
  // CourseNotesManager uses admin-only endpoints under the hood — make this clear.
  const canManage = user?.role === 'admin';
  const active = BRANDS.find((b) => b.id === tab) || BRANDS[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg,#0B0B0E 0%,#1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} data-testid="staff-notes-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Staff Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <BookOpenText className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Course Notes
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Upload as many PDFs per brand as you need — visible to installers at <code className="px-1 py-0.5 bg-white/10 rounded text-[11px]">/notes/probound</code>, <code className="px-1 py-0.5 bg-white/10 rounded text-[11px]">/notes/proepoxy</code>, <code className="px-1 py-0.5 bg-white/10 rounded text-[11px]">/notes/prodecor</code>.</p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-5 space-y-4">
        {!canManage && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="notes-readonly-banner">
            You\u2019re signed in as <strong>{user?.role}</strong> — Course Notes upload/delete is restricted to admins. You can still preview each brand below.
          </div>
        )}

        <div className="flex flex-wrap gap-2" data-testid="staff-notes-tabs">
          {BRANDS.map((b) => {
            const isActive = tab === b.id;
            return (
              <button
                key={b.id}
                onClick={() => setTab(b.id)}
                data-testid={`staff-notes-tab-${b.id}`}
                className={`px-3 py-2 rounded-lg text-sm font-bold border-2 transition-colors ${isActive ? 'text-white border-transparent shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}
                style={isActive ? { backgroundColor: b.color } : undefined}
              >
                {b.name}
              </button>
            );
          })}
        </div>

        {/* Re-key the manager on tab change so its internal state resets between
            brand tabs and the load() effect re-fires for the new brand. */}
        <CourseNotesManager
          key={active.id}
          brand={active.id}
          brandLabel={active.name}
          accent={active.accent}
        />
      </div>
    </div>
  );
}
